import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import {
  DataTableSkeleton,
  EntityIdentityCell,
  SectionCard,
  StatusPill,
} from "components/admin";
import { DataTable } from "components/molecules/table/table";
import { workspaceApi } from "pages/app/workspace/services";
import { SchoolTeamProfileTabProps } from "pages/app/workspace/school-team-profile/school-team-profile.types";
import {
  SchoolTeamProfileLabelText,
  SchoolTeamProfileMetaTextSecondary,
  SchoolTeamProfileOverviewStack,
} from "pages/app/workspace/styles/school-team-profile.page.inline.styles";
import { formatDateTime } from "utils/admin";
import { formatPortalLabel } from "utils/portal-access";

export const SchoolTeamActivityTab: React.FC<SchoolTeamProfileTabProps> = ({
  profileBackTo,
  schoolId,
  teamId,
}) => {
  const history = useHistory();
  const dashboardQuery = useQuery({
    queryKey: ["school-team-profile-dashboard", teamId],
    queryFn: () => workspaceApi.schoolTeamDashboards.get(teamId),
    enabled: Boolean(teamId),
  });
  const teamPlayersQuery = useQuery({
    queryKey: ["school-team-profile-players", teamId],
    queryFn: () => workspaceApi.schoolTeamPlayers.list(teamId),
    enabled: Boolean(teamId),
  });

  const recentPerformanceRecords = dashboardQuery.data?.recent_performance_records || [];
  const latestRosters = dashboardQuery.data?.latest_rosters || [];
  const roster = useMemo(
    () => teamPlayersQuery.data || dashboardQuery.data?.players || [],
    [dashboardQuery.data?.players, teamPlayersQuery.data],
  );

  const playersById = useMemo(() => {
    const directory = new Map<string, any>();

    roster.forEach((player: any) => {
      if (!player?.id) {
        return;
      }

      directory.set(String(player.id), player);
    });

    return directory;
  }, [roster]);

  return (
    <SchoolTeamProfileOverviewStack>
      <SectionCard
        subtitle="Latest logged outcomes for players on this squad."
        title="Recent performance"
      >
        <DataTable
          canEdit={false}
          canRemove={false}
          canView={Boolean(schoolId)}
          columns={[
            {
              label: "Player",
              accessor: (row: any) => {
                const playerId = row.player_id || row.player?.id;
                const player = row.player || (playerId ? playersById.get(String(playerId)) : null);

                return (
                  <EntityIdentityCell
                    fallbackLabel={row.player_name || player?.full_name}
                    imageAlt={row.player_name || player?.full_name || "Player"}
                    imageSrc={
                      row.player_avatar_url ||
                      row.avatar_url ||
                      player?.avatar_url ||
                      player?.image_url ||
                      player?.image
                    }
                    primaryText={row.player_name || player?.full_name || "Unknown"}
                    secondaryText={
                      [player?.position, player?.school_grade ? `Grade ${player.school_grade}` : null]
                        .filter(Boolean)
                        .join(" · ") || undefined
                    }
                    size="sm"
                    variant="athlete"
                  />
                );
              },
            },
            {
              label: "Record",
              accessor: (row: any) => (
                <div>
                  <SchoolTeamProfileLabelText>
                    {row.source_name || formatPortalLabel(row.record_type || "event")}
                  </SchoolTeamProfileLabelText>
                  <SchoolTeamProfileMetaTextSecondary>
                    {formatPortalLabel(row.source_type || "school")}
                    {row.sport_name ? ` · ${row.sport_name}` : ""}
                  </SchoolTeamProfileMetaTextSecondary>
                </div>
              ),
            },
            {
              label: "Recorded",
              accessor: (row: any) => formatDateTime(row.recorded_on),
            },
          ]}
          data={recentPerformanceRecords}
          emptyDescription="Performance records will appear here once coaches log outcomes."
          emptyTitle="No recent performance"
          hideNav
          isLoading={dashboardQuery.isLoading}
          loadingState={<DataTableSkeleton columns={3} rows={4} showThumbnail />}
          onView={(_, row) => {
            const playerId = row?.player_id || row?.player?.id;

            if (!schoolId || !playerId) {
              return;
            }

            history.push({
              pathname: `/workspace/school-players/${schoolId}/${playerId}`,
              state: { backTo: profileBackTo },
            });
          }}
          rowKey={(row: any) => row.id || `${row.player_id}-${row.recorded_on}`}
        />
      </SectionCard>

      <SectionCard
        subtitle="Match roster submissions linked to this team."
        title="Roster submissions"
      >
        <DataTable
          canEdit={false}
          canRemove={false}
          canView={Boolean(schoolId)}
          columns={[
            {
              label: "Fixture",
              accessor: (row: any) => (
                <div>
                  <SchoolTeamProfileLabelText>
                    {row.competition_name ||
                      row.match_label ||
                      row.school_match_name ||
                      "Match roster"}
                  </SchoolTeamProfileLabelText>
                  {row.opponent_name || row.opponent_team_name ? (
                    <SchoolTeamProfileMetaTextSecondary>
                      vs {row.opponent_name || row.opponent_team_name}
                    </SchoolTeamProfileMetaTextSecondary>
                  ) : null}
                </div>
              ),
            },
            {
              label: "Status",
              accessor: (row: any) => <StatusPill value={row.status || "draft"} />,
            },
            {
              label: "Squad",
              accessor: (row: any) => `${row.player_count ?? 0} players`,
            },
            {
              label: "Submitted",
              accessor: (row: any) => (
                <div>
                  <SchoolTeamProfileLabelText>
                    {formatDateTime(row.submitted_at)}
                  </SchoolTeamProfileLabelText>
                  {row.submitted_by_name ? (
                    <SchoolTeamProfileMetaTextSecondary>
                      {row.submitted_by_name}
                    </SchoolTeamProfileMetaTextSecondary>
                  ) : null}
                </div>
              ),
            },
          ]}
          data={latestRosters}
          emptyDescription="Roster submissions will surface here once they are created for this team."
          emptyTitle="No roster submissions"
          hideNav
          isLoading={dashboardQuery.isLoading}
          loadingState={<DataTableSkeleton columns={4} rows={3} />}
          onView={(_, row) => {
            const rosterId = row?.id;
            const matchId = row?.school_match_id || row?.match_id;

            if (!rosterId || !matchId) {
              return;
            }

            history.push({
              pathname: `/workspace/school-rosters/${schoolId}/${matchId}/${rosterId}`,
              state: { backTo: profileBackTo },
            });
          }}
          rowKey={(row: any) => row.id || `${row.school_match_id}-${row.submitted_at}`}
        />
      </SectionCard>
    </SchoolTeamProfileOverviewStack>
  );
};
